import { useState, useEffect } from 'react'
import { XMarkIcon, UserPlusIcon, ArrowPathIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { useToast } from './ToastContainer'
import { CustomRole } from '@/types'
import { getProjectAvailableRoles } from '../services/api/role'

interface User {
  id: string
  name: string
  email: string
  avatar?: string
  enabled: boolean
}

interface Group {
  id: string
  name: string
  description?: string
  color: string
  users: Array<{
    id: string
    user: {
      id: string
      name: string
      email: string
      avatar?: string
    }
  }>
  _count: {
    users: number
  }
}

interface InviteMemberModalProps {
  isOpen: boolean
  onClose: () => void
  projectId: string
  currentMembers: Array<{
    user: {
      id: string
      name: string
      email: string
    }
  }>
  onSuccess: () => void
}

const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  isOpen,
  onClose,
  projectId,
  currentMembers,
  onSuccess
}) => {
  const { showSuccess, showError } = useToast()
  const [allUsers, setAllUsers] = useState<User[]>([])
  const [allGroups, setAllGroups] = useState<Group[]>([])
  const [availableRoles, setAvailableRoles] = useState<CustomRole[]>([])
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([])
  const [selectedRoleId, setSelectedRoleId] = useState('')
  const [inviteMode, setInviteMode] = useState<'users' | 'groups'>('users')
  const [loading, setLoading] = useState(false)
  const [rolesLoading, setRolesLoading] = useState(false)
  const [usersLoading, setUsersLoading] = useState(false)
  const [groupsLoading, setGroupsLoading] = useState(false)
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const [groupSearchQuery, setGroupSearchQuery] = useState('')

  useEffect(() => {
    if (isOpen) {
      fetchUsers()
      fetchGroups()
      fetchProjectRoles()
    }
  }, [isOpen, projectId])

  const fetchUsers = async () => {
    try {
      setUsersLoading(true)
      const response = await fetch('/api/admin/users', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })

      if (!response.ok) {
        throw new Error('獲取使用者列表失敗')
      }

      const data = await response.json()
      setAllUsers(data.data?.users || [])
    } catch (error: any) {
      console.error('獲取使用者列表失敗:', error)
      showError('載入失敗', '無法載入使用者列表')
    } finally {
      setUsersLoading(false)
    }
  }

  const fetchGroups = async () => {
    try {
      setGroupsLoading(true)
      const response = await fetch('/api/groups', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })

      if (!response.ok) {
        throw new Error('獲取群組列表失敗')
      }

      const data = await response.json()
      setAllGroups(data.data?.groups || [])
    } catch (error: any) {
      console.error('獲取群組列表失敗:', error)
      showError('載入失敗', '無法載入群組列表')
    } finally {
      setGroupsLoading(false)
    }
  }

  const fetchProjectRoles = async () => {
    try {
      setRolesLoading(true)
      const response = await getProjectAvailableRoles(projectId)
      
      if (response.success) {
        const roles = response.data?.roles || []
        setAvailableRoles(roles)
        
        // 設定預設角色（優先順序：一般成員 > 成員 > 第一個角色）
        let defaultRole = roles.find(role => 
          role.name.includes('一般成員')
        )
        
        if (!defaultRole) {
          defaultRole = roles.find(role => 
            role.name.includes('成員')
          )
        }
        
        if (!defaultRole && roles.length > 0) {
          defaultRole = roles[0]
        }
        
        if (defaultRole) {
          setSelectedRoleId(defaultRole.id)
        }
      } else {
        throw new Error(response.error || '獲取專案角色失敗')
      }
    } catch (error: any) {
      console.error('獲取專案角色失敗:', error)
      showError('載入失敗', '無法載入專案角色')
    } finally {
      setRolesLoading(false)
    }
  }

  const getAvailableUsers = () => {
    const memberUserIds = currentMembers.map(m => m.user.id)
    return allUsers.filter(user => {
      // 檢查是否啟用且不是現有成員
      if (!user.enabled || memberUserIds.includes(user.id)) {
        return false
      }
      
      // 檢查是否符合搜尋條件
      if (userSearchQuery.trim()) {
        const query = userSearchQuery.toLowerCase()
        return (
          user.name.toLowerCase().includes(query) ||
          user.email.toLowerCase().includes(query)
        )
      }
      
      return true
    })
  }

  const getFilteredGroups = () => {
    if (!groupSearchQuery.trim()) {
      return allGroups
    }
    
    const query = groupSearchQuery.toLowerCase()
    return allGroups.filter(group => 
      group.name.toLowerCase().includes(query) ||
      (group.description && group.description.toLowerCase().includes(query))
    )
  }

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds(prev => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    )
  }

  const toggleGroupSelection = (groupId: string) => {
    setSelectedGroupIds(prev => 
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    )
  }

  const getGroupMemberUserIds = () => {
    const groupMemberIds = new Set<string>()
    selectedGroupIds.forEach(groupId => {
      const group = allGroups.find(g => g.id === groupId)
      if (group) {
        group.users.forEach(u => groupMemberIds.add(u.user.id))
      }
    })
    return Array.from(groupMemberIds)
  }

  const handleInvite = async () => {
    const finalUserIds = inviteMode === 'users' 
      ? selectedUserIds 
      : getGroupMemberUserIds()
    
    if (finalUserIds.length === 0 || !selectedRoleId) {
      if (!selectedRoleId) {
        showError('邀請失敗', '請選擇角色')
      } else if (inviteMode === 'users') {
        showError('邀請失敗', '請選擇要邀請的使用者')
      } else {
        showError('邀請失敗', '請選擇要邀請的群組')
      }
      return
    }

    setLoading(true)
    try {
      const response = await fetch(`/api/projects/admin/${projectId}/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ 
          userIds: finalUserIds,
          roleId: selectedRoleId,
          inviteMode: inviteMode,
          groupIds: inviteMode === 'groups' ? selectedGroupIds : undefined
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || '邀請成員失敗')
      }

      const data = await response.json()
      showSuccess('邀請成功', data.message || '成員邀請成功')
      
      // 重置狀態
      setSelectedUserIds([])
      setSelectedGroupIds([])
      setSelectedRoleId('')
      
      onSuccess()
      onClose()
    } catch (error: any) {
      showError('邀請失敗', error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setSelectedUserIds([])
    setSelectedGroupIds([])
    setSelectedRoleId('')
    setInviteMode('users')
    setUserSearchQuery('')
    setGroupSearchQuery('')
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <UserPlusIcon className="w-5 h-5 mr-2" />
              邀請專案成員
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Mode Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                邀請方式
              </label>
              <div className="flex rounded-md shadow-sm">
                <button
                  type="button"
                  onClick={() => setInviteMode('users')}
                  className={`px-4 py-2 text-sm font-medium rounded-l-md border ${
                    inviteMode === 'users'
                      ? 'bg-primary-50 border-primary-200 text-primary-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  按使用者邀請
                </button>
                <button
                  type="button"
                  onClick={() => setInviteMode('groups')}
                  className={`px-4 py-2 text-sm font-medium rounded-r-md border-l-0 border ${
                    inviteMode === 'groups'
                      ? 'bg-primary-50 border-primary-200 text-primary-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  按群組邀請
                </button>
              </div>
            </div>

            {/* Role Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                選擇角色 <span className="text-red-500">*</span>
              </label>
              {rolesLoading ? (
                <div className="flex items-center justify-center py-4">
                  <ArrowPathIcon className="w-5 h-5 animate-spin text-gray-400" />
                  <span className="ml-2 text-sm text-gray-500">載入角色中...</span>
                </div>
              ) : (
                <select
                  value={selectedRoleId}
                  onChange={(e) => setSelectedRoleId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">請選擇角色</option>
                  {availableRoles.map(role => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                      {role.description && ` - ${role.description}`}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* User/Group Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {inviteMode === 'users' ? '選擇使用者' : '選擇群組'} <span className="text-red-500">*</span>
              </label>
              
              {inviteMode === 'users' ? (
                // Users Selection
                <div className="space-y-3">
                  {/* User Search Box */}
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      placeholder="搜尋使用者名稱或電子郵件..."
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>

                  {usersLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <ArrowPathIcon className="w-5 h-5 animate-spin text-gray-400" />
                      <span className="ml-2 text-sm text-gray-500">載入使用者中...</span>
                    </div>
                  ) : (
                    <div className="max-h-64 overflow-y-auto space-y-2 border border-gray-200 rounded-md p-3">
                      {getAvailableUsers().map((user) => (
                      <label key={user.id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <div className="ml-3 flex items-center space-x-3 flex-1">
                          {user.avatar ? (
                            <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-600">
                                {user.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{user.name}</p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                        </div>
                      </label>
                    ))}
                    
                    {getAvailableUsers().length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">
                        {userSearchQuery.trim() 
                          ? '找不到符合搜尋條件的使用者' 
                          : '沒有可邀請的使用者'}
                      </p>
                    )}
                  </div>
                )}
              </div>
              ) : (
                // Groups Selection
                <div className="space-y-3">
                  {/* Group Search Box */}
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={groupSearchQuery}
                      onChange={(e) => setGroupSearchQuery(e.target.value)}
                      placeholder="搜尋群組名稱或描述..."
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>

                  {groupsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <ArrowPathIcon className="w-5 h-5 animate-spin text-gray-400" />
                      <span className="ml-2 text-sm text-gray-500">載入群組中...</span>
                    </div>
                  ) : (
                    <div className="max-h-64 overflow-y-auto space-y-2 border border-gray-200 rounded-md p-3">
                      {getFilteredGroups().map((group) => (
                      <label key={group.id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedGroupIds.includes(group.id)}
                          onChange={() => toggleGroupSelection(group.id)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <div className="ml-3 flex items-center space-x-3 flex-1">
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                            style={{ backgroundColor: group.color }}
                          >
                            {group.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{group.name}</p>
                            <p className="text-xs text-gray-500">
                              {group.description || `${group._count.users} 位成員`}
                            </p>
                          </div>
                        </div>
                      </label>
                    ))}
                    
                    {getFilteredGroups().length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">
                        {groupSearchQuery.trim() 
                          ? '找不到符合搜尋條件的群組' 
                          : '沒有可用的群組'}
                      </p>
                    )}
                  </div>
                )}
              </div>
              )}
              
              {inviteMode === 'users' && selectedUserIds.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  已選擇 {selectedUserIds.length} 位使用者
                </p>
              )}
              
              {inviteMode === 'groups' && selectedGroupIds.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  已選擇 {selectedGroupIds.length} 個群組 (共 {getGroupMemberUserIds().length} 位使用者)
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              取消
            </button>
            <button
              onClick={handleInvite}
              disabled={
                loading || 
                !selectedRoleId || 
                (inviteMode === 'users' ? selectedUserIds.length === 0 : selectedGroupIds.length === 0) ||
                usersLoading || 
                rolesLoading || 
                groupsLoading
              }
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? '邀請中...' : inviteMode === 'users' 
                ? `邀請 ${selectedUserIds.length} 位成員`
                : `邀請 ${getGroupMemberUserIds().length} 位成員 (來自 ${selectedGroupIds.length} 個群組)`
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default InviteMemberModal